import { useEffect, useState, useRef, useCallback } from "react";
import { api, addWsListener } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { MessageSquare, Users, Plus, Send, Smile, ArrowLeft, Loader2, User, Check, Shield, Search, Trash2 } from "lucide-react";
import { Link, Navigate } from "react-router-dom";
import { BBC_LOGO_URL } from "@/lib/brand";
import ProfilePhotoWidget from "@/components/ProfilePhotoWidget";
import NotificationBell from "@/components/NotificationBell";

const BASIC_EMOJIS = ["😀", "🙏", "❤️", "🙌", "😊", "✨", "✝️", "⛪", "📖", "🕊️"];
const REACTION_EMOJIS = ["👍", "❤️", "🙏", "😄", "😮", "😢"];

const safeFormatTime = (dateStr) => {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? "" : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

const safeFormatDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? "" : d.toLocaleDateString();
  } catch {
    return "";
  }
};

export default function Messages() {
  const { user: authUser } = useAuth();
  const [activeTab, setActiveTab] = useState("dm"); // "dm" or "group"
  const [threads, setThreads] = useState([]);
  const [groups, setGroups] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [loadingList, setLoadingList] = useState(true);

  // Active Chat State
  const [activeChat, setActiveChat] = useState(null); // { mode: "dm"|"group", id: string, name: string }
  const [messages, setMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  // Modals
  const [showNewDmModal, setShowNewDmModal] = useState(false);
  const [showNewGroupModal, setShowNewGroupModal] = useState(false);

  // Group creation form
  const [groupName, setGroupName] = useState("");
  const [groupDesc, setGroupDesc] = useState("");
  const [selectedMembers, setSelectedMembers] = useState({});
  const [creatingGroup, setCreatingGroup] = useState(false);

  // Directory filter search query
  const [dirSearch, setDirSearch] = useState("");

  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const lastFetchAt = useRef(null);

  const isManager = authUser && (authUser.role === "super_admin" || authUser.role === "admin" || authUser.role === "staff");
  const isSuperAdmin = authUser?.role === "super_admin";

  const upsertMessage = useCallback((message) => {
    setMessages(prev => {
      const index = prev.findIndex(x => x.id === message.id);
      if (index >= 0) {
        const next = [...prev];
        next[index] = { ...next[index], ...message };
        return next;
      }
      return [...prev, message];
    });
  }, []);

  const replaceMessageById = useCallback((message) => {
    setMessages(prev => prev.map(x => x.id === message.id ? { ...x, ...message } : x));
  }, []);

  // Load tabs lists
  const loadLists = useCallback(async () => {
    try {
      const [{ data: dmRes }, { data: groupRes }] = await Promise.all([
        api.get("/dm/threads"),
        api.get("/groups")
      ]);
      setThreads(dmRes || []);
      setGroups(groupRes || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingList(false);
    }
  }, []);

  // Fetch Directory for starting chats/groups
  const loadDirectory = useCallback(async () => {
    try {
      const { data } = await api.get("/directory");
      setDirectory(data || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    if (authUser) {
      loadLists();
      loadDirectory();
    }
  }, [authUser, loadLists, loadDirectory]);

  // Load chat messages
  const loadMessages = useCallback(async (fresh = false) => {
    if (!activeChat) return;
    try {
      const currentSince = fresh ? undefined : lastFetchAt.current;
      if (activeChat.mode === "group") {
        const { data } = await api.get(`/groups/${activeChat.id}/messages${currentSince ? `?since=${encodeURIComponent(currentSince)}` : ""}`);
        if (fresh) {
          setMessages(data.messages || []);
        } else if (data.messages && data.messages.length) {
          setMessages(prev => {
            const seen = new Set(prev.map(x => x.id));
            const newMsgs = data.messages.filter(x => !seen.has(x.id));
            if (!newMsgs.length) return prev;
            return [...prev, ...newMsgs];
          });
        }
        if (data.messages && data.messages.length) {
          lastFetchAt.current = data.messages[data.messages.length - 1].created_at;
        }
      } else {
        const { data } = await api.get(`/dm/${activeChat.id}${currentSince ? `?since=${encodeURIComponent(currentSince)}` : ""}`);
        if (fresh) {
          setMessages(data.messages || []);
        } else if (data.messages && data.messages.length) {
          setMessages(prev => {
            const seen = new Set(prev.map(x => x.id));
            const newMsgs = data.messages.filter(x => !seen.has(x.id));
            if (!newMsgs.length) return prev;
            return [...prev, ...newMsgs];
          });
        }
        if (data.messages && data.messages.length) {
          lastFetchAt.current = data.messages[data.messages.length - 1].created_at;
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      if (fresh) setChatLoading(false);
    }
  }, [activeChat]);

  // WebSocket listener
  useEffect(() => {
    if (!authUser) return;

    const removeListener = addWsListener((msg) => {
      // 1. Handle incoming messages for active chat
      if (activeChat) {
        if (activeChat.mode === "group" && msg.type === "group_message" && msg.group_id === activeChat.id) {
          upsertMessage(msg.message);
          api.post(`/groups/${activeChat.id}/read`).catch(() => {});
        } else if (activeChat.mode === "dm" && msg.type === "dm_message" && msg.thread_key === [authUser.user_id, activeChat.id].sort().join("|")) {
          upsertMessage(msg.message);
          api.post(`/dm/${activeChat.id}/read`).catch(() => {});
        } else if (activeChat.mode === "group" && msg.type === "group_message_deleted" && msg.group_id === activeChat.id) {
          replaceMessageById(msg.message);
        } else if (activeChat.mode === "dm" && msg.type === "dm_message_deleted" && msg.thread_key === [authUser.user_id, activeChat.id].sort().join("|")) {
          replaceMessageById(msg.message);
        }
      }

      // 2. Refresh lists for unread indicators/last message
      if (msg.type === "group_message" || msg.type === "dm_message" || msg.type === "group_message_deleted" || msg.type === "dm_message_deleted") {
        loadLists();
      }
    });

    return () => removeListener();
  }, [authUser, activeChat, loadLists, upsertMessage, replaceMessageById]);

  // Polling for new messages (Removed in favor of WS, but keeping a slow fallback for reliability)
  useEffect(() => {
    if (activeChat) {
      setChatLoading(true);
      lastFetchAt.current = null;
      loadMessages(true).then(() => {
        setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
          }
        }, 150);
      });

      clearInterval(pollRef.current);
      pollRef.current = setInterval(() => {
        loadMessages(false);
      }, 15000); // Slow fallback polling
    } else {
      clearInterval(pollRef.current);
      setMessages([]);
    }
    return () => clearInterval(pollRef.current);
  }, [activeChat, loadMessages]);

  // Auto scroll down on new messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  if (!authUser) return <Navigate to="/admin/login" replace />;

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t || !activeChat) return;

    setSending(true);
    setText("");
    const replyId = replyTo?.id;
    setReplyTo(null);

    try {
      if (activeChat.mode === "group") {
        const { data } = await api.post(`/groups/${activeChat.id}/messages`, { text: t, reply_to_id: replyId });
        upsertMessage(data);
      } else {
        const { data } = await api.post(`/dm/${activeChat.id}`, { text: t, reply_to_id: replyId });
        upsertMessage(data);
      }
      loadLists();
    } catch (err) {
      toast.error("Failed to send message");
      setText(t);
    } finally {
      setSending(false);
    }
  };

  const handleReact = async (msgId, emoji) => {
    try {
      const { data } = await api.post(`/chat/messages/${msgId}/react`, { emoji });
      setMessages(msgs => msgs.map(m => m.id === msgId ? { ...m, reactions: data.reactions } : m));
    } catch (err) {
      toast.error("Could not add reaction");
    }
  };

  const canDeleteMessage = (message) => {
    if (message.is_deleted) return false;
    const ownerId = message.sender_id || message.user_id;
    return isSuperAdmin || ownerId === authUser?.user_id;
  };

  const handleDeleteMessage = async (message) => {
    if (!window.confirm("Delete message?\n\nThis message will be removed from the conversation.")) return;
    try {
      const { data } = await api.delete(`/chat/messages/${message.id}`);
      replaceMessageById(data.message);
      toast.success("Message deleted");
      loadLists();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete message");
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!groupName.trim()) return toast.error("Group name required");
    setCreatingGroup(true);
    try {
      const memberIds = Object.keys(selectedMembers).filter(k => selectedMembers[k]);
      await api.post("/groups", {
        name: groupName.trim(),
        description: groupDesc.trim(),
        member_ids: memberIds
      });
      toast.success("Group created successfully");
      setShowNewGroupModal(false);
      setGroupName("");
      setGroupDesc("");
      setSelectedMembers({});
      loadLists();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create group");
    } finally {
      setCreatingGroup(false);
    }
  };

  const startDm = (u) => {
    setActiveChat({ mode: "dm", id: u.user_id, name: u.name });
    setShowNewDmModal(false);
    setDirSearch("");
  };

  const filteredDirectory = directory.filter(u =>
    !dirSearch ||
    u.name?.toLowerCase().includes(dirSearch.toLowerCase()) ||
    u.email?.toLowerCase().includes(dirSearch.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex flex-col h-screen overflow-hidden">
      {/* Navbar Header */}
      <header className="bg-white border-b border-[#E5E0D8] flex-shrink-0">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src={BBC_LOGO_URL} alt="" className="w-8 h-8" />
            <span className="font-serif-display text-xl text-[#2C4C3B]">Fellowship Chat</span>
          </div>
          <div className="flex gap-4 text-sm items-center">
            <Link to="/" className="text-[#5C6661] hover:text-[#2C4C3B] font-medium">Home</Link>
            {isManager && <Link to="/admin" className="text-[#5C6661] hover:text-[#2C4C3B] font-medium">CMS</Link>}
            <NotificationBell />
            <ProfilePhotoWidget user={authUser} compact />
          </div>
        </div>
      </header>

      {/* Main Container Layout */}
      <div className="flex-1 flex overflow-hidden max-w-7xl w-full mx-auto p-4 lg:p-6 gap-6">

        {/* Left Sidebar - Chat List (Hidden on mobile if chat is active) */}
        <div className={`w-full md:w-80 bg-white border border-[#E5E0D8] rounded-2xl flex flex-col overflow-hidden flex-shrink-0 ${activeChat ? "hidden md:flex" : "flex"}`}>

          {/* Navigation Tabs */}
          <div className="flex border-b border-[#E5E0D8] bg-[#FAF9F6]">
            <button
              onClick={() => setActiveTab("dm")}
              className={`flex-1 py-3 text-sm font-medium inline-flex items-center justify-center gap-2 border-b-2 transition-all ${activeTab === "dm" ? "border-[#2C4C3B] text-[#2C4C3B] bg-white font-semibold" : "border-transparent text-[#5C6661] hover:text-[#1C201E]"}`}
            >
              <MessageSquare className="w-4 h-4" /> Private
            </button>
            <button
              onClick={() => setActiveTab("group")}
              className={`flex-1 py-3 text-sm font-medium inline-flex items-center justify-center gap-2 border-b-2 transition-all ${activeTab === "group" ? "border-[#2C4C3B] text-[#2C4C3B] bg-white font-semibold" : "border-transparent text-[#5C6661] hover:text-[#1C201E]"}`}
            >
              <Users className="w-4 h-4" /> Groups
            </button>
          </div>

          {/* Action Header Button */}
          <div className="p-3 border-b border-[#E5E0D8] flex items-center justify-between bg-white">
            <span className="text-xs uppercase tracking-wider text-[#8A8F8B] font-semibold pl-2">
              {activeTab === "dm" ? "Conversations" : "Channels"}
            </span>
            {activeTab === "dm" ? (
              <button onClick={() => setShowNewDmModal(true)} className="p-1.5 rounded-full hover:bg-[#FAF9F6] text-[#2C4C3B] transition-colors" title="New Message">
                <Plus className="w-4 h-4" />
              </button>
            ) : (
              isManager && (
                <button onClick={() => setShowNewGroupModal(true)} className="p-1.5 rounded-full hover:bg-[#FAF9F6] text-[#2C4C3B] transition-colors" title="Create Group">
                  <Plus className="w-4 h-4" />
                </button>
              )
            )}
          </div>

          {/* List View Scroll Content */}
          <div className="flex-1 overflow-y-auto divide-y divide-[#FAF9F6]">
            {loadingList ? (
              <div className="flex items-center justify-center py-10 text-[#5C6661] text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading conversations…</div>
            ) : activeTab === "dm" ? (
              threads.length === 0 ? (
                <div className="p-8 text-center text-xs text-[#8A8F8B]">No private messages yet. Click + to start.</div>
              ) : (
                threads.map(t => (
                  <button
                    key={t.user_id}
                    onClick={() => setActiveChat({ mode: "dm", id: t.user_id, name: t.name })}
                    className={`w-full text-left p-3.5 flex items-center gap-3 transition-colors ${activeChat?.id === t.user_id ? "bg-[#2C4C3B]/5 border-r-4 border-[#2C4C3B]" : "hover:bg-[#FAF9F6]"}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-[#2C4C3B]/10 flex items-center justify-center font-bold text-[#2C4C3B] text-sm flex-shrink-0">
                      {(t.name || "?").slice(0, 1).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-baseline mb-0.5">
                        <h4 className="text-sm font-semibold text-[#1C201E] truncate">{t.name}</h4>
                        <span className="text-[10px] text-[#8A8F8B]">{safeFormatDate(t.last_at)}</span>
                      </div>
                      <p className="text-xs text-[#5C6661] truncate">{t.last_from_me ? "You: " : ""}{t.last_text}</p>
                    </div>
                  </button>
                ))
              )
            ) : (
              groups.length === 0 ? (
                <div className="p-8 text-center text-xs text-[#8A8F8B]">You are not in any chat groups yet.</div>
              ) : (
                groups.map(g => (
                  <button
                    key={g.id}
                    onClick={() => setActiveChat({ mode: "group", id: g.id, name: g.name })}
                    className={`w-full text-left p-3.5 flex flex-col gap-1 transition-colors ${activeChat?.id === g.id ? "bg-[#2C4C3B]/5 border-r-4 border-[#2C4C3B]" : "hover:bg-[#FAF9F6]"}`}
                  >
                    <div className="flex justify-between items-center">
                      <h4 className="text-sm font-bold text-[#1C201E] truncate"># {g.name}</h4>
                      <span className="text-[10px] uppercase bg-[#E5E0D8]/50 px-1.5 py-0.5 rounded text-[#5C6661] font-mono">{(g.member_ids || []).length} users</span>
                    </div>
                    {g.description && <p className="text-xs text-[#8A8F8B] truncate">{g.description}</p>}
                  </button>
                ))
              )
            )}
          </div>
        </div>

        {/* Right Active Chat Workspace Window */}
        <div className={`flex-1 bg-white border border-[#E5E0D8] rounded-2xl flex flex-col overflow-hidden ${!activeChat ? "hidden md:flex items-center justify-center p-8 bg-[#FAF9F6]/30 border-dashed" : "flex"}`}>

          {activeChat ? (
            <>
              {/* Chat Title Window Bar Header */}
              <div className="px-4 py-3 border-b border-[#E5E0D8] bg-white flex items-center gap-3 flex-shrink-0">
                <button onClick={() => setActiveChat(null)} className="p-1 rounded-full hover:bg-[#FAF9F6] text-[#5C6661] md:hidden">
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="w-9 h-9 rounded-full bg-[#2C4C3B] text-white flex items-center justify-center font-bold text-sm">
                  {activeChat.mode === "group" ? "#" : activeChat.name.slice(0, 1).toUpperCase()}
                </div>
                <div>
                  <h3 className="text-sm font-bold text-[#1C201E]">{activeChat.name}</h3>
                  <p className="text-[11px] text-[#5C6661] capitalize">{activeChat.mode} conversation</p>
                </div>
              </div>

              {/* Chat Messages Log Board Area */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3.5 bg-[#FAF9F6]/40">
                {chatLoading ? (
                  <div className="flex items-center justify-center py-20 text-[#5C6661] text-sm"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading transcript…</div>
                ) : messages.length === 0 ? (
                  <div className="py-20 text-center text-xs text-[#8A8F8B] italic">No messages in this chat conversation. Send a message to start fellowship!</div>
                ) : (
                  messages.map(m => {
                    const isMine = (m.sender_id || m.user_id) === authUser?.user_id;
                    const isDeleted = !!m.is_deleted;
                    const showDeletedAudit = isDeleted && isSuperAdmin;
                    return (
                      <div key={m.id} className={`flex flex-col ${isMine ? "items-end" : "items-start"}`}>
                        {/* Sender Display Name */}
                        {!isMine && activeChat.mode === "group" && (
                          <span className="flex items-center gap-2 text-[10px] font-semibold text-[#5C6661] mb-1 ml-2"><ProfilePhotoWidget user={{name:m.sender_name,picture:m.sender_picture || m.user_picture}} compact />{m.sender_name}</span>
                        )}

                        {/* Chat Bubble Container Wrapper */}
                        <div className="group relative max-w-[75%]">

                          {/* Inside Message Text Content Block */}
                          <div className={`p-3 rounded-2xl shadow-sm text-sm relative transition-all ${isMine ? "bg-[#2C4C3B] text-white rounded-tr-none" : "bg-white text-[#1C201E] border border-[#E5E0D8] rounded-tl-none"} ${isDeleted ? (showDeletedAudit ? "border border-red-300 bg-red-50 text-red-800" : "opacity-70 italic") : ""}`}>
                            {isDeleted && (
                              <div className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black tracking-widest mb-1 ${showDeletedAudit ? "bg-red-100 text-red-800" : "bg-black/10 text-current"}`}>
                                DELETED
                              </div>
                            )}
                            <p className="break-words leading-relaxed whitespace-pre-line">{m.text}</p>
                            {showDeletedAudit && (
                              <p className="mt-1 text-[10px] font-semibold text-red-700">
                                {m.deletion_type === "moderation" ? "Moderator deleted" : "Sender deleted"}
                                {m.deleted_by_name ? ` by ${m.deleted_by_name}` : ""}
                                {m.deleted_at ? ` · ${new Date(m.deleted_at).toLocaleString()}` : ""}
                              </p>
                            )}

                            {/* Render Reactions Row */}
                            {!isDeleted && m.reactions && Object.keys(m.reactions).length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1.5">
                                {Object.entries(m.reactions).map(([emoji, uids]) => (
                                  <div key={emoji} className="bg-black/5 dark:bg-white/10 text-[10px] px-1.5 py-0.5 rounded-full inline-flex items-center gap-1 font-sans">
                                    <span>{emoji}</span>
                                    <span className="font-semibold">{uids.length}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Quick Emoji Reaction Popup Over Hover Block Menu */}
                          {!isDeleted && <div className={`absolute top-0 opacity-0 group-hover:opacity-100 transition-opacity flex bg-white border border-[#E5E0D8] rounded-full p-1 shadow-md z-10 gap-0.5 transform -translate-y-2/3 ${isMine ? "right-0" : "left-0"}`}>
                            {!isDeleted && REACTION_EMOJIS.map(emoji => (
                              <button
                                key={emoji}
                                onClick={() => handleReact(m.id, emoji)}
                                className="w-6 h-6 hover:bg-[#FAF9F6] rounded-full text-xs flex items-center justify-center"
                              >
                                {emoji}
                              </button>
                            ))}
                            {canDeleteMessage(m) && (
                              <button
                                onClick={() => handleDeleteMessage(m)}
                                className="w-6 h-6 hover:bg-red-50 rounded-full text-xs flex items-center justify-center text-red-600"
                                title="Delete message"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>}
                        </div>

                        {/* Timestamp Log Block Row */}
                        <span className="text-[9px] text-[#8A8F8B] mt-1 px-1">
                          {safeFormatTime(m.created_at)}
                        </span>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Chat Input Bar Footer Composer */}
              <form onSubmit={handleSendMessage} className="p-3 border-t border-[#E5E0D8] bg-white flex flex-col gap-2 flex-shrink-0">

                {/* Emoji short tray line selection row */}
                <div className="flex items-center gap-1 overflow-x-auto pb-1 max-w-full no-scrollbar">
                  <button
                    type="button"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className="p-1 rounded text-[#5C6661] hover:bg-[#FAF9F6] flex-shrink-0 mr-1"
                  >
                    <Smile className="w-4 h-4" />
                  </button>
                  {BASIC_EMOJIS.map(emoji => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => setText(prev => prev + emoji)}
                      className="px-1.5 py-0.5 text-sm hover:bg-[#FAF9F6] rounded transition-colors flex-shrink-0"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>

                {/* Main typing bar entry container row */}
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={text}
                    onChange={e => setText(e.target.value)}
                    placeholder="Type your message here for fellowship…"
                    className="flex-1 px-4 py-2.5 bg-[#FAF9F6] border border-[#E5E0D8] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2C4C3B] focus:bg-white transition-all text-[#1C201E]"
                  />
                  <button
                    type="submit"
                    disabled={sending || !text.trim()}
                    className="p-2.5 rounded-xl bg-[#2C4C3B] text-white hover:bg-[#1A3326] transition-colors disabled:opacity-40 disabled:hover:bg-[#2C4C3B] flex-shrink-0 flex items-center justify-center min-w-[42px]"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="text-center max-w-sm flex flex-col items-center">
              <div className="w-16 h-16 rounded-full bg-[#2C4C3B]/10 flex items-center justify-center text-[#2C4C3B] mb-4">
                <MessageSquare className="w-8 h-8" />
              </div>
              <h3 className="font-serif-display text-2xl text-[#1C201E] mb-2">Your Conversations</h3>
              <p className="text-sm text-[#5C6661] leading-relaxed">
                Select an existing conversation thread from the left panel sidebar or press the "+" icon to browse church members and start messaging!
              </p>
            </div>
          )}
        </div>
      </div>

      {/* MODAL 1: NEW PRIVATE MESSAGE PICKER */}
      {showNewDmModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => setShowNewDmModal(false)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl w-full max-w-md flex flex-col max-h-[85vh] overflow-hidden">
            <div className="p-4 border-b border-[#E5E0D8] flex justify-between items-center bg-[#FAF9F6]">
              <h3 className="font-serif-display text-xl text-[#1C201E]">New Private Message</h3>
              <button onClick={() => setShowNewDmModal(false)} className="text-sm font-medium text-[#5C6661] hover:text-black">Cancel</button>
            </div>
            <div className="p-3 border-b border-[#E5E0D8] relative">
              <Search className="w-4 h-4 absolute left-6 top-6 text-[#8A8F8B]" />
              <input
                type="text"
                value={dirSearch}
                onChange={e => setDirSearch(e.target.value)}
                placeholder="Search people by name or email…"
                className="w-full pl-9 pr-4 py-2 border border-[#E5E0D8] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]"
              />
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-[#FAF9F6] p-2">
              {filteredDirectory.map(u => (
                <button
                  key={u.user_id}
                  onClick={() => startDm(u)}
                  className="w-full text-left p-3 hover:bg-[#FAF9F6] rounded-xl flex items-center gap-3 transition-colors group"
                >
                  <div className="w-9 h-9 rounded-full bg-[#FAF9F6] border border-[#E5E0D8] flex items-center justify-center font-bold text-[#2C4C3B] text-xs">
                    {u.name.slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#1C201E] group-hover:text-[#2C4C3B] truncate">{u.name}</div>
                    <div className="text-xs text-[#5C6661] truncate">{u.email} · <span className="capitalize">{u.role === "bible_school_student" ? "Bible School Student" : u.role}</span></div>
                  </div>
                </button>
              ))}
              {filteredDirectory.length === 0 && (
                <div className="p-6 text-center text-xs text-[#8A8F8B]">No users matched your directory search query.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODAL 2: CREATE CHAT GROUP MODAL */}
      {showNewGroupModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => setShowNewGroupModal(false)}>
          <form onSubmit={handleCreateGroup} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl w-full max-w-md flex flex-col max-h-[85vh] overflow-hidden">
            <div className="p-4 border-b border-[#E5E0D8] flex justify-between items-center bg-[#FAF9F6]">
              <h3 className="font-serif-display text-xl text-[#1C201E]">Create Fellowship Group</h3>
              <button type="button" onClick={() => setShowNewGroupModal(false)} className="text-sm font-medium text-[#5C6661] hover:text-black">Cancel</button>
            </div>

            <div className="p-4 space-y-3.5 border-b border-[#E5E0D8] overflow-y-auto">
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#5C6661] mb-1 font-semibold">Group Name *</label>
                <input
                  type="text"
                  required
                  value={groupName}
                  onChange={e => setGroupName(e.target.value)}
                  placeholder="e.g., Youth Ministry, Choir, Roster A"
                  className="w-full px-3 py-2 border border-[#E5E0D8] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#5C6661] mb-1 font-semibold">Description (Optional)</label>
                <input
                  type="text"
                  value={groupDesc}
                  onChange={e => setGroupDesc(e.target.value)}
                  placeholder="Short explanation of group purpose…"
                  className="w-full px-3 py-2 border border-[#E5E0D8] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]"
                />
              </div>
            </div>

            <div className="p-2 bg-[#FAF9F6] border-b border-[#E5E0D8]">
              <span className="text-xs uppercase tracking-wider text-[#5C6661] font-semibold pl-2">Select Group Members</span>
            </div>

            <div className="flex-1 overflow-y-auto divide-y divide-[#FAF9F6] p-2 max-h-[30vh]">
              {directory.map(u => (
                <div
                  key={u.user_id}
                  onClick={() => setSelectedMembers(prev => ({ ...prev, [u.user_id]: !prev[u.user_id] }))}
                  className="p-2.5 hover:bg-[#FAF9F6] rounded-xl flex items-center justify-between cursor-pointer transition-colors"
                >
                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <div className="w-8 h-8 rounded-full bg-[#2C4C3B]/5 flex items-center justify-center font-bold text-xs text-[#2C4C3B]">
                      {u.name.slice(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-semibold text-[#1C201E] truncate">{u.name}</div>
                      <div className="text-[10px] text-[#5C6661] truncate">{u.email}</div>
                    </div>
                  </div>
                  <div className={`w-5 h-5 rounded-md border flex items-center justify-center transition-colors flex-shrink-0 ${selectedMembers[u.user_id] ? "bg-[#2C4C3B] border-[#2C4C3B]" : "border-[#E5E0D8] bg-white"}`}>
                    {selectedMembers[u.user_id] && <Check className="w-3 h-3 text-white stroke-[3px]" />}
                  </div>
                </div>
              ))}
            </div>

            <div className="p-4 bg-[#FAF9F6] border-t border-[#E5E0D8] text-right">
              <button
                type="submit"
                disabled={creatingGroup || !groupName.trim()}
                className="px-5 py-2 rounded-full bg-[#2C4C3B] text-white text-xs font-semibold hover:bg-[#1A3326] transition-colors disabled:opacity-50 inline-flex items-center"
              >
                {creatingGroup ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null} Create Group
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
